/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import React from 'react';
import PropTypes from 'prop-types';
// eslint-disable-next-line import/no-extraneous-dependencies
import { ComboBox } from 'carbon-components-react';
import { defineMessages, injectIntl } from 'react-intl';
import { useField } from 'formik';
import isEmpty from 'lodash/isEmpty';
import noop from 'lodash/noop';

import { filterComboboxItems } from '../ComboBoxFormik/comboBoxUtils';

const propTypes = {
  /** what placeholder text should we show */
  placeholder: PropTypes.string,
  // path to field in formik context that we want to target with this input
  id: PropTypes.string.isRequired,
  // eslint-disable-next-line react/boolean-prop-naming
  disabled: PropTypes.bool,
  // eslint-disable-next-line react/forbid-prop-types
  items: PropTypes.array,
  itemToString: PropTypes.func,
  onInputChange: PropTypes.func,
  onChange: PropTypes.func,
};

const defaultProps = {
  disabled: false,
  placeholder: '',
  items: [],
  itemToString: item => item?.text || '',
  onInputChange: noop,
  onChange: noop,
};

const messages = defineMessages({
  'close.menu': 'Close menu',
  'open.menu': 'Open menu',
  'clear.all': 'Clear all',
  'clear.selection': 'Clear selection',
});

/**
 * Simple implementation of a Combobox that updates the Formik form state
 * based on the onChange function in Combobox
 */
const SimpleComboBoxFormik = ({
  id,
  disabled,
  placeholder,
  items,
  itemToString,
  onInputChange,
  onChange,
  intl: { formatMessage },
  ...others
}) => {
  const [, meta, helpers] = useField({ name: id });
  return (
    <ComboBox
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...others}
      items={items}
      id={id}
      placeholder={placeholder}
      onChange={({ selectedItem }) => {
        helpers.setValue(selectedItem?.text || '');
        if (onChange) {
          onChange(selectedItem);
        }
      }}
      itemToString={itemToString}
      onInputChange={onInputChange}
      disabled={disabled}
      invalid={!isEmpty(meta?.error)}
      shouldFilterItem={filterComboboxItems}
      invalidText={meta.error}
      translateWithId={idToTranslate => formatMessage(messages[idToTranslate])}
    />
  );
};

SimpleComboBoxFormik.propTypes = propTypes;
SimpleComboBoxFormik.defaultProps = defaultProps;

export default injectIntl(SimpleComboBoxFormik);
